import { ChangeEvent, useEffect, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import TextInput from '../../BaseComponents/TextInput';
import TextArea from '../../BaseComponents/TextArea';
import Button from '../../BaseComponents/Button';
import './TeamModal.scss';
import Loader from '../../BaseComponents/Loader';
import { createTeam, getTeamById, updateTeam, Team } from '../../../api/teams';
import { toast } from 'react-toastify';

type TeamModalProps = {
  isOpen: boolean;
  onClose: () => void;
  teamToEdit: Team | null;
  onSaved: (team: Team, mode: 'create' | 'update') => void;
};

interface FormValues {
  name: string;
  description: string;
}

const TeamModal: React.FC<TeamModalProps> = ({
  isOpen,
  onClose,
  teamToEdit,
  onSaved,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isFormError, setIsFormError] = useState(false);
  const [editTeamDetails, setEditTeamDetails] = useState<Team | null>(teamToEdit);

  useEffect(() => {
    if (!isOpen) return;
    if (!teamToEdit?.id) {
      setEditTeamDetails(null);
      return;
    }

    const fetchTeamDetails = async () => {
      setIsDetailsLoading(true);
      try {
        const data = await getTeamById(teamToEdit.id);
        setEditTeamDetails(data);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load team details');
        setEditTeamDetails(teamToEdit);
      } finally {
        setIsDetailsLoading(false);
      }
    };

    fetchTeamDetails();
  }, [isOpen, teamToEdit]);

  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name.trim()) {
      errors.name = 'Name is required';
    } else if (values.name.trim().length < 2) {
      errors.name = 'Name is too short';
    }
    if (!values.description.trim()) {
      errors.description = 'Description is required';
    }
    return errors;
  };

  const initialValues: FormValues = editTeamDetails
    ? {
        name: editTeamDetails.name,
        description: editTeamDetails.description,
      }
    : { name: '', description: '' };

  const formik = useFormik<FormValues>({
    initialValues,
    validate,
    onSubmit: async (values, { resetForm }) => {
      if (isFormError) setIsFormError(false);
      setIsLoading(true);
      try {
        const payload = {
          name: values.name.trim(),
          description: values.description.trim(),
        };
        if (teamToEdit) {
          const updated = await updateTeam(teamToEdit.id, payload);
          onSaved(updated, 'update');
          toast.success('Team updated');
        } else {
          const created = await createTeam(payload);
          onSaved(created, 'create');
          toast.success('Team added');
        }
        onClose();
        resetForm();
      } catch (error: any) {
        setIsFormError(true);
        toast.error(error?.message || 'Something went wrong');
      } finally {
        setIsLoading(false);
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
  });

  const handleFieldChange = <T extends keyof FormValues>(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: T
  ) => {
    if (formik.errors[name] && formik.submitCount > 0) {
      formik.setFieldError(name, '');
    }
    formik.handleChange(e);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const isSubmitDisabled =
    isLoading ||
    !formik.values.name.trim() ||
    !formik.values.description.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={teamToEdit ? 'Update team' : 'Add team'}
      width='520px'
    >
      {isDetailsLoading ? (
        <Loader />
      ) : (
        <FormikProvider value={formik}>
          <form
            onSubmit={formik.handleSubmit}
            noValidate
            className='team-modal-container'
          >
            <div className='main-info-container'>
              <div className='section-title'>Team details</div>
              <div className='inputs-wrapper'>
                <TextInput
                  name='name'
                  title='Name *'
                  placeholder='Enter name'
                  type='text'
                  isError={formik.submitCount > 0 && !!formik.errors.name}
                  errorMessage={
                    formik.submitCount > 0 ? formik.errors.name : undefined
                  }
                  onChange={handleFieldChange}
                  value={formik.values.name}
                />
                <TextArea
                  name='description'
                  title='Description *'
                  placeholder='Enter description'
                  isError={formik.submitCount > 0 && !!formik.errors.description}
                  errorMessage={
                    formik.submitCount > 0
                      ? formik.errors.description
                      : undefined
                  }
                  onChange={handleFieldChange}
                  value={formik.values.description}
                  rows={4}
                />
              </div>
            </div>
            <div className='btn-wrapper'>
              <Button
                text={teamToEdit ? 'Update team' : 'Add team'}
                type='submit'
                isLoading={isLoading}
                isDisabled={isSubmitDisabled}
              />
              {isFormError && (
                <div className='error-message'>
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </form>
        </FormikProvider>
      )}
    </Modal>
  );
};

export default TeamModal;
